import React, { useEffect, useRef, useState } from 'react'
import { ApiDTO_NTSPform, NTSP_StatPortalProps,  NTSPElement } from './Ntsp_Slice'
import { PatientDetailsHeader } from './PatientHeader'
import DatePicker from '../../GenericComponents/DatePicker'
import moment from 'moment'
import { tzCodeToIANA } from '../../Utils'
import { FoundationsButton } from '../../GenericComponents/button'
import {
    AcuteServiceKey,
    SabacuteServiceKey,
    teleEEGServiceKey,
    validateForm,
    convertEasternToLocal,
    Neurohospitalistkey
} from './NTSP_StatPortal'
import { getCaseIdSuffix, getServiceLineName } from './CaseDetails'
import { FieldErrors } from 'react-hook-form/dist/types/errors'
import { usePromiseDebounce } from '../../Hooks/usePromiseDebounce' //FP-12575 Debounce save for UI actions

const PatientEducation: React.FC<NTSP_StatPortalProps> = ({
    form,
    setForm,
    NTSPElements,
    handleSave,
    handleClear,
    handleTabSwitch,
    validationSchema,
    errors,
    setErrors,
    isSaved,
    providerNotes,
}) => {
    if (errors === undefined) errors = {}
    if (setErrors === undefined) setErrors = (errors: any) => {}
    if (validationSchema === undefined) validationSchema = {}
    const formRef = useRef(null as any)
    const [submitted, setSubmitted] = useState<boolean>(false)
    const [saving, setSaving] = useState<boolean>(false)
    const [selectedCaseId, setSelectedCaseId] = useState<string>(form.CaseId);
    const [formError, setFormError] = useState<string>('')
    const debouncedHandleSave = usePromiseDebounce(async (...args: Parameters<typeof handleSave>) => {
        return await Promise.resolve(handleSave(...args));
    }); //FP-12575 debounce to prevent multiple rapid clicks    
    const wrapHandleSave = async () => {
        setSubmitted(true)
        setSaving(true)
        const tempErrors = validateForm(form, validationSchema)
        // Merge existing errors (including date validation errors) with new validation errors
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)
        // Check if there are any errors (from validation schema or date validation)
        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            setSaving(false)
            return
        } else {
            setErrors({})
            setFormError('')
            if (debouncedHandleSave) {
                await debouncedHandleSave(); //FP-12575 using debounced handle save to prevent multiple rapid clicks on save button, but await it to ensure completion before proceeding
                setSaving(false)
            }
            return
        }
    }
    const wrapHandleClear = async () => {
        setSubmitted(true)
        setSaving(true)
        const tempErrors = validateForm(form, validationSchema)
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)
        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            setSaving(false)
            return
        } else {
            setErrors({})
            setFormError('')
            if (handleSave) {
                await handleSave(true)
            }
            if (handleClear) handleClear()
            setSaving(false)
            return
        }
    }
    useEffect(() => {
        if (!Object.values(errors) || Object.values(errors).join('').length === 0) {
            setFormError('')
        }
    }, [errors])
    
    const handleChange = (e: any) => {
        const { name, value, type } = e.target

        setForm((prev: ApiDTO_NTSPform) => ({
            ...prev,
            [name]: value,
        }))
        const tempErrors = { ...errors }
        const schema = validationSchema[name]
        tempErrors[name] = typeof schema === 'function' ? schema(value) : schema || ''
        setErrors(tempErrors)

        if (
            submitted &&
            Object.values(tempErrors) &&
            Object.values(tempErrors).join('').length > 0
        ) {
            setFormError('Please fix the errors before saving.')
            return
        } else {
            setFormError('')
        }
        
    }
    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target
        if (validationSchema[name]) {
            setErrors((prev: { [s: string]: string }) => ({
                ...prev,
                [name]: validationSchema[name](value),
            }))
        }
    }

    // Handler for date field validation errors from DatePicker
    const handleDateValidationError = (fieldName: string) => (error: string) => {
        setErrors((prev: { [s: string]: string }) => ({
            ...prev,
            [fieldName]: error,
        }))
    }

    const providerNotesDisabled = providerNotes?.find(n => n.CaseId === form.CaseId)?.ProviderCaseNotes?.find
                                        (note =>note.Note.indexOf('(Patient Education)') !== -1)?.Id !== undefined

    // Check if site supports Acute and SubAcute
    const isSiteAcuteAndSubAcute = form.fk_Facility !== -1 && 
        NTSPElements?.Facilities?.some(
            (facility) =>
                facility._key === form.fk_Facility &&
                facility.SiteStatus?.toLowerCase() === 'acute and subacute'
        );

    return (
        <>
            <PatientDetailsHeader
                form={form}
                NTSPElements={NTSPElements}
            />

            <form
                onSubmit={async (e) => {
                    e.preventDefault()
                    wrapHandleSave()
                    
                }}
                className="ntsp-form-container"
                ref={formRef}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '2rem',
                        flexWrap: 'wrap',
                    }}
                >
                    {/* Column 1 */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1.2rem',
                            width: '32rem',
                        }}
                    >
                        <div className="form-group" style={{ display: 'flex', justifyContent: 'space-between'}}>
                            <label htmlFor="ts_EducationTime">
                                Patient Education Time: <br />
                                <span style={{ fontSize: '11px', color: '#666' }}>
                                    (Patient Facility Time)
                                </span>
                            </label>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <DatePicker
                                    id="ts_EducationTime"
                                    name="ts_EducationTime"
                                    dateFormat="MM/dd/yyyy HH:mm"
                                    tabIndex={10}
                                    //className="ntsp-fields"
                                    inputClass="ntsp-fields"
                                    showMasking={true}
                                    timeformat="HH:mm"
                                    showTime={true}
                                    disabled={providerNotesDisabled}
                                    startingDate={
                                        form.ts_EducationTime
                                            ? new Date(form.ts_EducationTime)
                                            : null
                                    }
                                    setStartingDate={(dateString) => {
                                        const event = {
                                            target: {
                                                name: 'ts_EducationTime',
                                                value: dateString,
                                                type: 'text',
                                            },
                                        }
                                        handleChange(event)
                                        if (handleBlur) {
                                            handleBlur(event as any)
                                        }
                                    }}
                                    interval={15}
                                    placeholder="Select Date & Time"
                                    style={{ width: '155px', display: 'inline-block' }}
                                    isClearable={true}
                                    calendarClosed={true}
                                    onValidationError={handleDateValidationError('ts_EducationTime')}
                                />
                                {/* <span className="mx-1" style={{ lineHeight: "2.1", fontSize: "12px" }}>{IANAToTzCode(timezone)}</span> */}
                                <button
                                    type={'button'}
                                    className="d-inline-block app-button"
                                    tabIndex={10}
                                    disabled={providerNotesDisabled}
                                    onClick={() => {
                                        const selectedFacility = NTSPElements?.Facilities?.find(
                                            (f) => f._key === form.fk_Facility
                                        )
                                        const ianaTz = selectedFacility?.TZCode
                                            ? tzCodeToIANA(selectedFacility.TZCode)
                                            : undefined
                                        const now = ianaTz
                                            ? moment.tz(new Date(), ianaTz)
                                            : moment(new Date())
                                        setForm((prev: ApiDTO_NTSPform) => ({
                                            ...prev,
                                            ts_EducationTime: now.format('YYYY-MM-DDTHH:mm'),
                                        }))
                                    }}
                                >
                                    Now
                                </button>
                            </span>
                            {errors.ts_EducationTime && (
                                <div
                                    style={{
                                        color: 'red',
                                        fontSize: '12px',
                                        marginTop: '4px',
                                        maxWidth: '155px',
                                    }}
                                >
                                    {errors.ts_EducationTime + ''}
                                </div>
                            )}
                        </div>
                        <div >
                            <label htmlFor="EducationNotes">New Education Notes:</label>
                            <textarea
                                id="NewNote" //FP-14388 chnge ide so placeholder in light gray color
                                name="EducationNotes"
                                value={form.EducationNotes}
                                onChange={handleChange}
                                maxLength={2048}
                                onResize={() => {}}
                                rows={3}
                                // disabled={providerNotesDisabled} //FP-14388 Allow editing education notes even if there are existing provider notes
                                placeholder="Enter Notes"
                                // title= {providerNotesDisabled ? "Notes are already entered": ""}//FP-14388 Remove tooltip to indicate notes can be entered even if provider notes exist
                                style={{
                                    border: '1px solid #888',
                                    borderRadius: '2px',
                                    padding: '12px',
                                    height: '125px',
                                    width: '100%',
                                    paddingBottom: '1.5rem',
                                    boxSizing: 'border-box',
                                    cursor: "text", //FP-14388 Allow editing education notes even if there are existing provider notes
                                }}
                            />
                        </div>
                    </div>
                   
                    {/* Column 2 */}
                    <div style={{                      
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1.2rem',
                            minWidth: '32rem',
                    }}>
                        <div className="form-group" style={{ display: 'flex', justifyContent: 'space-between'}}>
                            <label htmlFor="fk_Outcome">Status/Type:</label>
                            <select
                                className="ntsp-fields"
                                id="fk_Outcome"
                                name="fk_Outcome"
                                value={form.fk_Outcome}
                                onChange={handleChange}
                                style={{ minWidth: '220px' }}
                            >
                                <option value="-1">-Select Status-</option>
                                {NTSPElements?.Outcome?.map((Status: NTSPElement) => (
                                    <option key={Status._key} value={Status._key}>
                                        {Status.FriendlyName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    
                     {/* Highlights & Plan label above textarea */}
                    <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px', width: '100%' }}>
                        <label htmlFor="HighlightsPlan" style={{ marginBottom: '0.5rem' }}
                            className={!isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey && form.fk_ConsultService!==Neurohospitalistkey ? 'disabled' : ''}>
                            Assessment/Plan: {/*FP-14502 replace Highlights & Plan with Assessment/Plan as per SME request*/}
                        </label>
                        <textarea
                            id="HighlightsPlan"
                            className="w-100"
                            style={{
                                height: '125px',
                                overflowWrap: 'break-word',
                                cursor: !isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey && form.fk_ConsultService!==Neurohospitalistkey ? 'not-allowed' : 'text',
                            }}
                            maxLength={2048}
                            disabled={form.fk_ConsultService!==Neurohospitalistkey && (!isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey)}
                            title={!isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey && form.fk_ConsultService!==Neurohospitalistkey ? "Disabled because the consult service is not Subacute or Neurohospitalist and site does not support Acute and SubAcute" : ""}
                            placeholder="Enter assessment and plan" //FP-14502 placeholder text updated as per SME request
                            value={form.HighlightsPlan || ''}
                            onChange={handleChange}
                            name='HighlightsPlan'
                        >{form.HighlightsPlan}
                        </textarea>
                    </div>
                    </div>
                    </div>
                    {/* if site supports PSFS then show this followup option*/}
                    {(NTSPElements?.Facilities?.filter(f=>f._key ==form.fk_Facility && f.IsPSFS)?.length ?? 0) > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBlock: '1.2rem', width: '32rem' , justifyContent: 'space-between'}}>
                            <label htmlFor="fk_PtEduFollowUp" style={{ minWidth: '110px', marginBottom: 0 }}>Follow Up:</label>
                            <select
                                className="ntsp-fields"
                                id="fk_PtEduFollowUp"
                                name="fk_PtEduFollowUp"
                                value={form.fk_PtEduFollowUp}
                                onChange={handleChange}
                                style={{ minWidth: '220px' }}
                                disabled={form.IsPSFSExists}
                            >
                                <option value="-1">-Select FollowUp-</option>
                                {NTSPElements?.PatientEduFollowUp?.map((Status: NTSPElement) => (
                                    <option key={Status._key} value={Status._key}>
                                        {Status.FriendlyName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}    
                <div style={{ marginTop: '8px' }}>
                    <label htmlFor="ClinicalNotes" className="mb-1">
                        Provider Clinical Notes:
                    </label>
                    <div
                        className='provider-clinical-notes mt-2'
                    >
                        <div className="tab_section">
                            <span className="ntsp_tab-buttons w-100">
                            {providerNotes?.map((noteGroup, idx) => {
                                let tabLabel = '';
                                if (NTSPElements?.FollowUp && noteGroup.fk_FollowUp!=-1) {
                                    const followUpObj = NTSPElements.FollowUp.find(f => f._key === noteGroup.fk_FollowUp);
                                    tabLabel = followUpObj ? followUpObj.FriendlyName :"Followup";
                                }
                                else if (NTSPElements?.ConsultServices){
                                     const serviceLineObj = NTSPElements.ConsultServices.find(f => f._key === noteGroup.fk_ConsultService);
                                    tabLabel = serviceLineObj ? serviceLineObj.FriendlyName:getServiceLineName(form.fk_ConsultService);
                                }
                                return (
                                    <button
                                        key={noteGroup.CaseId}
                                        type='button'
                                        // className= ''{ 
                                        //     'pcn-tab' +
                                        //     (noteGroup.CaseId === selectedCaseId ? ' active' : '')
                                        // }
                                        style={{minWidth:"15rem"}}
                                        className={`py-2 ntsp_tab-button notes-tab ${selectedCaseId === noteGroup.CaseId ? 'active' : ''}`}
                                        onClick={() => setSelectedCaseId(noteGroup.CaseId)}
                                    >
                                        {tabLabel}
                                        <br />
                                        {noteGroup.CaseId !== form.CaseId ? (
                                            <>
                                                Case : #{getCaseIdSuffix(noteGroup.CaseId)}
                                            </>
                                        ) : (
                                            <div> (current)</div>
                                        )}
                                    </button>
                                );
                            })}
                            </span>
                        </div>
                        <div className='clinical-Notes pcn-notes-area'>
                            {                           
                                providerNotes
                                    ?.find(n => n.CaseId === selectedCaseId)
                                    ?.ProviderCaseNotes.map((note, idx) => {
                                        // Get facility time zone
                                        const selectedFacility = NTSPElements?.Facilities?.find(f => f._key === form.fk_Facility);
                                        const ianaTz = selectedFacility?.TZCode ? tzCodeToIANA(selectedFacility.TZCode) : undefined;
                                        const noteTime = moment(convertEasternToLocal(note.ts_CreationDateTime, ianaTz))?.format('MM/DD/YYYY HH:mm') ;
                                        return (
                                            <div
                                                className='pcn-note'
                                                key={note.Id || idx}
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(180px, 180px) 1fr',
                                                    alignItems: 'center',
                                                    //gap: '1rem',
                                                    marginBottom: '0.35rem',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                <span style={{ color: '#888', fontSize: '12px', fontWeight: 500, display: 'inline-block' }}>
                                                    {note.PostedByUserFullName}
                                                    {noteTime && (
                                                        <span style={{ color: '#888', fontSize: '12px', marginLeft: 6 }}>
                                                            ({noteTime}):
                                                        </span>
                                                    )}
                                                </span>
                                                <span style={{ whiteSpace: 'pre-wrap', marginLeft: 6 }}>
                                                    {note.ExamCompleted ?  '(Exam Completed) ' : ''}
                                                    {Array.isArray(note.fk_CPTCodes) && note.fk_CPTCodes.length > 0 && note.fk_CPTCodes.filter(code => code !== -1).map((code, idx) => {
                                                        const cpt = NTSPElements?.CPTCodes?.find(c => c._key === code);
                                                        return ` (Selected CPT Codes: ${cpt?.FriendlyName || code})`;
                                                    })}
                                                    {note.Note}
                                                </span>
                                            </div>
                                        );
                                    })
                            }
                        </div>
                    </div>
                </div>
                {/* Buttons */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1.2rem',
                        marginTop: '2rem',
                    }}
                >
                    <FoundationsButton
                        type="submit"
                        buttonClasses="app-button"
                        tabIndex={17}
                        id={''}
                        disabled={saving}
                    >
                        Save
                    </FoundationsButton>
                    <FoundationsButton
                        type="button"
                        buttonClasses="app-button"
                        tabIndex={18}
                        onClick={wrapHandleClear}
                        id={''}
                        toolTipText="Currently opened case entry form will be saved and closed."
                        tooltipClasses="info-icon"
                    >
                        Close
                    </FoundationsButton>
                </div>
                {submitted && formError !== '' && (
                    <div style={{ color: 'red', textAlign: 'right', marginTop: '1rem' }}>
                        {formError}
                    </div>
                )}
            </form>
        </>
    )
}

export default PatientEducation
